import * as React from "react";
import { cn } from "../../utility/utils";
import { Controller } from "react-hook-form";
import { Label } from ".";

const Input = React.forwardRef(
  (
    {
      className,
      parentClassName,
      type,
      label,
      control,
      rules,
      name,
      isPositiveNumber,
      isInteger,
      isCurrency,
      isTextarea,
      onChangeFn,
      onBlurFn,
      blockUntilOnChangeIsTrue,
      disabled = false, 
      hideLabel = false,
      ...props
    },
    ref
  ) => {
    // Prevent scroll wheel from changing number input values
    const handleWheel = React.useCallback(
      (e) => {
        if (type === "number") {
          // Prevent the input value from changing
          e.target.blur();
        }
      },
      [type]
    );

    const onKeyDown = (e) => {
      if (type === "number") {
        const allowedChars = [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
        ];

        if (!isPositiveNumber) {
          allowedChars.push("-"); // If not positive number, allow negative sign

          if (e.key === "-") {
            if (e.target.value.includes("-") || e.target.selectionStart !== 0) {
              e.preventDefault();
            }
            return;
          }
        }

        if (!isInteger) {
          // Allow only one decimal point
          if (e.key === ".") {
            if (e.target.value.includes(".")) {
              e.preventDefault();
            }
            return;
          }
        }

        if (
          /^[0-9]$/.test(e.key) ||
          allowedChars.includes(e.key) ||
          (e.key === "v" && (e.ctrlKey || e.metaKey)) || // Allow pasting
          (e.key === "c" && (e.ctrlKey || e.metaKey)) || // Allow copying
          (e.key === "a" && (e.ctrlKey || e.metaKey)) || // Allow selecting all
          (e.key === "r" && (e.ctrlKey || e.metaKey)) // Allow reloading
        ) {
          return true;
        } else {
          e.preventDefault();
        }
      }
    };

    const validateInputOnChange = (e) => {
      const val = e.target.value;
      if (type === "number") {
        if (props.min && parseFloat(val) < props.min) {
          return false;
        }
        if (props.max && parseFloat(val) > props.max) {
          return false;
        }
      }
      return true;
    };

    const onTextPaste = (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedData = e.target.value + clipboardData.getData("Text"); // Check if entire value is valid

      if (type === "number") {
        let regex;
        if (isInteger) {
          regex = isPositiveNumber ? /^[0-9]+$/ : /^-?[0-9]+$/;
        } else {
          regex = isPositiveNumber
            ? /^[0-9]*\.?[0-9]*$/
            : /^-?[0-9]*\.?[0-9]*$/;
        }

        if (!regex.test(pastedData)) {
          e.preventDefault();
        }
      }
    };

    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <>
            <div className={"flex flex-col space-y-2 " + parentClassName}>
              {!hideLabel && (
                <Label>
                  {label}
                  {rules?.required && label && (
                    <span className=" text-red-600">*</span>
                  )}
                </Label>
              )}
              <div className="relative flex">
                {isCurrency ? (
                  <>
                    <span className={cn(
                      "inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 text-gray-500 text-sm font-medium",
                      disabled ? "bg-gray-100" : "bg-gray-50"
                    )}>
                      ₹
                    </span>
                    <input
                      type={type === "number" ? "text" : type}
                      className={cn(
                        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                        disabled ? "bg-gray-100 cursor-not-allowed" : "",
                        className
                      )}
                      onKeyDown={onKeyDown}
                      ref={ref}
                      value={value}
                      onPaste={onTextPaste}
                      onBlur={(e) => {
                        onBlur();
                        onBlurFn?.(e);
                      }}
                      onChange={(e) => {
                        if (!disabled && validateInputOnChange(e)) {
                          if (blockUntilOnChangeIsTrue) {
                            if (onChangeFn?.(e)) {
                              onChange(e);
                            }
                          } else {
                            onChangeFn?.(e);
                            onChange(e);
                          }
                        }
                      }}
                      onWheel={handleWheel}
                      disabled={disabled}
                      {...props}
                    />
                  </>
                ) : isTextarea ? (
                  <textarea
                    className={cn(
                      "flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none",
                      disabled ? "bg-gray-100 cursor-not-allowed" : "",
                      className
                    )}
                    ref={ref}
                    value={value}
                    onBlur={(e) => {
                      onBlur();
                      onBlurFn?.(e);
                    }}
                    onChange={(e) => {
                      if (!disabled) {
                        if (blockUntilOnChangeIsTrue) {
                          if (onChangeFn?.(e)) {
                            onChange(e);
                          }
                        } else {
                          onChangeFn?.(e);
                          onChange(e);
                        }
                      }
                    }}
                    disabled={disabled}
                    {...props}
                  />
                ) : (
                  <input
                    type={type === "number" ? "text" : type}
                    className={cn(
                      "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                      disabled ? "bg-gray-100 cursor-not-allowed" : "",
                      className
                    )}
                    onKeyDown={onKeyDown}
                    ref={ref}
                    value={value}
                    onPaste={onTextPaste}
                    onBlur={(e) => {
                      onBlur();
                      onBlurFn?.(e);
                    }}
                    onChange={(e) => {
                      if (!disabled && validateInputOnChange(e)) {
                        if (blockUntilOnChangeIsTrue) {
                          if (onChangeFn?.(e)) {
                            onChange(e);
                          }
                        } else {
                          onChangeFn?.(e);
                          onChange(e);
                        }
                      }
                    }}
                    onWheel={handleWheel}
                    disabled={disabled}
                    {...props}
                  />
                )}
              </div>
              {error && (
                <small className="text-red-600 font-medium text-sm">
                  {error.message}
                </small>
              )}
            </div>
          </>
        )}
      />
    );
  }
);
Input.displayName = "Input";

export default Input;
